import { Box, useDisclosure } from "@chakra-ui/react";
import { FC, Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import CustomModal from "../../../components/CustomModal/CustomModal";
import { GLAMLabHeader } from "../../../components/GLAMLabHeader/GLAMLabHeader";
import { HairColorCarousel } from "../../../components/HairColorCarrousel/HairColorCarousel";
import { HairColorTonality } from "../../../components/HairColorTonality/HairColorTonality";
import { Header } from "../../../components/Header/Header";
import { InteractiveButtons } from "../../../components/InteractiveButtons/InteractiveButtons";
import { ProcessingImage } from "../../../components/ProcessingImage/ProcessingImage";
import { ProductBannerOnColorVTO } from "../../../components/ProductBannerOnColorVTO/ProductBannerOnColorVTO";
import { SmallLoader } from "../../../components/SmallLoader/SmallLoader";
import usePage from "../../../hooks/usePage";
import useScreen from "../../../hooks/useScreen";
import useSession from "../../../hooks/useSession";
import { useSourcePhoto } from "../../../hooks/useSourcePhoto";
import { State } from "../../../interfaces/State";
import { Channel } from "../../../interfaces/tracking";
import type { AppDispatch } from "../../../reducers/configureStore";
import {
  setCountClickNewArrivals,
  setIsOpenSWW,
} from "../../../reducers/global/globalSlice";
import { setIsNecessaryChange } from "../../../reducers/volume/volumeSlice";
import { fetchConfig } from "../../../services/config";
import { fetchSaveSelfie, fetchUploadSelfie } from "../../../services/selfie";
import { BACKGROUND_IMAGE } from "../../../utils/constants";
import { hangExperienceEnterVTO } from "../../../utils/hangExperience/hangExperienceEnterVTO";
import { Analytics, EventNames } from "../../../utils/tracking";
import volumeDataProcessor from "../../../utils/volumeDataProcessor";

export interface VtoColorPageProps {
  from: string;
}

const paths = [{ text: "Color Try-On", url: "" }];

export const VtoColor: FC<VtoColorPageProps> = (props) => {
  const dispatch = useDispatch<AppDispatch>();
  const { redirectTo } = usePage();
  const location = useLocation();
  const platform: string =
    new URLSearchParams(location.search).get("platform") ?? "";
  const navigate = useNavigate();
  const {
    colors,
    volumes,
    brands,
    appliedImage,
    selectedColor,
    storedPhoto,
    showSmallLoader,

    isError: isErrorColor,
    selectedSubColor,
    loader,
  } = useSelector((state: State) => state.volume);
  const { isError, isUpload, capture } = useSelector(
    (state: State) => state.selfie
  );
  const selfieId = sessionStorage.getItem("vtoSelfieId");
  const { isMobile } = useScreen();
  const { currentUser, isLoggedIn } = useSession();
  const [showOriginalPhoto, setShowOriginalPhoto] = useState(false);
  const [isByColor, setFilter] = useState<boolean | undefined>(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loaderStartTime] = useState(new Date().getTime());
  const { isOpen: isOpen, onOpen: onOpen, onClose: onClose } = useDisclosure();
  const sourcePhoto = useSourcePhoto();

  useEffect(() => {
    try {
      dispatch(setIsNecessaryChange(true));
      dispatch(fetchConfig());
      dispatch(setCountClickNewArrivals(0));
      if (!selfieId) dispatch(fetchUploadSelfie(capture));
    } catch (error: any) {
      console.log("error: ", error);
    }
  }, []);
  useEffect(() => {
    let amountColors: number = 0;
    colors?.forEach((color) => {
      amountColors += color?.colors?.length ?? 0;
    });

    if (colors?.length && volumes?.length) {
      const from = (() => {
        const currentURL = window.location.href.search("from=VTO") !== -1;
        if (currentURL) return "VTO";
        else return "Home";
      })();
      Analytics.event(Channel.GA, EventNames.COLOR_LOADED, {
        login: isLoggedIn(),
        device: platform.includes("g-mob")
          ? "app"
          : isMobile
          ? "mobile"
          : "desktop",
        user_id: currentUser?.gti,
        source: platform.includes("g-mob")
          ? "native"
          : isMobile
          ? "mobile"
          : "desktop",
        categories: colors?.length ?? 0,
        color: amountColors,
        volume: volumes?.length ?? 0,
        origin: new URLSearchParams(location.search).get("origin"),
        trails: 1,
        from: from,
      });
    }
  }, [colors?.length, volumes?.length]);

  useEffect(() => {
    if (isError || isErrorColor) {
      navigate(-1);
      dispatch(setIsOpenSWW(true));
      document.body.style.overflow = "scroll";
    }
  }, [isError, isErrorColor]);

  useEffect(() => {
    if (isLoading) {
      document.body.style.overflow = "hidden";
      Analytics.event(Channel.GA, EventNames.LOADER_STARTED, {
        device: platform.includes("g-mob")
          ? "app"
          : isMobile
          ? "mobile"
          : "desktop",
        option: paths[0].text,
        source: platform.includes("g-mob")
          ? "native"
          : isMobile
          ? "mobile"
          : "desktop",
        counter: (new Date().getTime() - loaderStartTime) / 1000 + "s",
      });
    } else {
      document.body.style.overflow = "scroll";
    }
  }, [isLoading]);

  volumeDataProcessor({ setIsLoading, isByColor });

  window.addEventListener("beforeunload", function (e) {
    var confirmationMessage =
      "It looks like you have been editing something. " +
      "If you leave before saving, your changes will be lost.";
    (e || window.event).returnValue = confirmationMessage; //Gecko + IE
    return confirmationMessage; //Gecko + Webkit, Safari, Chrome etc.
  });

  const handleShowOriginalPhoto = () => {
    setShowOriginalPhoto(!showOriginalPhoto);
  };

  const getDisplayedImage = () => {
    if (showOriginalPhoto) {
      return storedPhoto ?? "";
    } else if (appliedImage) {
      return appliedImage;
    } else {
      return "";
    }
  };

  const savePhoto = async () => {
    dispatch(fetchSaveSelfie(selfieId));
    onClose();
    Analytics.event(Channel.GA, EventNames.SAVE_PHOTO_LOADED, {
      device: platform.includes("g-mob")
        ? "app"
        : isMobile
        ? "mobile"
        : "desktop",
      source: paths[0].text,
      user_id: currentUser?.gti,
    });
    navigate({ search: "page=take-photo&try-on=color" });
  };

  useEffect(() => {
    hangExperienceEnterVTO();
  }, []);

  if (isLoading) {
    return <ProcessingImage vto={redirectTo} />;
  }

  return (
    <Fragment>
      <Box
        minH="100vh"
        bgImage={{
          base: "none",
          md: process.env.PUBLIC_URL + BACKGROUND_IMAGE,
        }}
        backgroundAttachment="fixed"
        bgSize="cover"
      >
        <Box
          display="flex"
          flexDirection="column"
          alignItems={{ base: "inherit", md: "center" }}
        >
          {!isMobile ? (
            <GLAMLabHeader paths={paths} />
          ) : (
            <Header
              title={props.from}
              screen={paths[0].text}
            />
          )}

          <Box
            bg="#FFFFFF"
            borderLeft={{ md: "1px solid #E6E6E6" }}
            borderRight={{ md: "1px solid #E6E6E6" }}
            borderBottom={{ md: "1px solid #E6E6E6" }}
            w="100%"
            pb={{ md: "30px" }}
            mb={{ md: "100px" }}
            maxWidth={{ base: "100%", md: "1110px" }}
            display={{ base: "block", md: "flex" }}
            flexDirection={{ md: "column" }}
            alignItems="center"
            position="relative"
          >
            {showSmallLoader ? <SmallLoader vto="styles" /> : null}

            {!showSmallLoader ? (
              <Box
                position="relative"
                minW={{ md: "700px", base: "auto" }}
                justifyContent="center"
                display="flex"
                height={isMobile ? "411px" : "auto"}
              >
                <InteractiveButtons
                  gnomeId=""
                  photo={getDisplayedImage()}
                  handleShowOriginalPhoto={handleShowOriginalPhoto}
                  showOriginalPhoto={showOriginalPhoto}
                />
                <Box
                  px="10px"
                  position="relative"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  flexDirection="column"
                  w={{ base: "100%", md: "max-content" }}
                  maxWidth="512px"
                  gap="70px"
                  mb="20px"
                >
                  <div
                    style={
                      isMobile
                        ? {
                            width: "100%",
                            height: "491px",
                            marginTop: "20px",
                            backgroundImage: `url(${`${
                              getDisplayedImage() as string
                            }`})`,
                            position: "relative",
                            backgroundSize: "contain",
                            backgroundPosition: "center bottom",
                            backgroundRepeat: "no-repeat",
                          }
                        : {
                            width: "512px",
                            height: "512px",
                            marginTop: "20px",
                            backgroundImage: `url(${`${
                              getDisplayedImage() as string
                            }`})`,
                            position: "relative",
                            backgroundSize: "contain",
                            backgroundPosition: "center bottom",
                            backgroundRepeat: "no-repeat",
                          }
                    }
                  />

                </Box>
              </Box>
            ) : null}

            <HairColorCarousel
              colorList={isByColor ? colors : brands}
              selectedColor={selectedColor}
              screen={paths[0].text}
              disable={loader}
              isByColor={isByColor}
              setFilter={setFilter}
            />
            {selectedColor?.colors && (
              <HairColorTonality
                colorList={selectedColor.colors}
                selectedColor={selectedSubColor}
                screen={paths[0].text}
                disable={loader}
                isByColor={isByColor}
              />
            )}

            {selectedColor && selectedSubColor && (
              <ProductBannerOnColorVTO
                product={selectedSubColor.product}
                color={selectedSubColor.id}
              />
            )}

            {/* <HairServices screen={paths[0].text} />

            <ProductsRecommendationsCarousel screen={paths[0].text} /> */}
          </Box>
        </Box>
      </Box>
      <CustomModal
        isOpen={isOpen}
        onClose={() => {
          onClose();
          navigate({ search: "page=take-photo&try-on=color" });
        }}
        onConfirm={savePhoto}
        title="Would you like to save this photo for late use?"
        screen={paths[0].text}
      />
    </Fragment>
  );
};
